import {
  ApiEpInstagram,
  ApiEpOrganization,
  ApiEpPageConfigOld,
  ApiEpSocialAccount,
  ApiEpSocialPageGroup,
  IApiEpPageConfigOld,
} from "@smp/api";
import { useSocialPageGroupSetQuery } from "@smp/graphql";
import { dataArrayFromMap } from "@smp/utils";
import produce from "immer";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import { useQueryGetPageConfigSetAllInvalidate } from "@/queries";

import { ContextEp } from "./ep.context";
import { IContextEp } from "./types";

export const ContextProviderEp = ({ children }: PropsWithChildren<{}>) => {
  const queryClient = useQueryClient();

  const invalidatePageConfigSet = useQueryGetPageConfigSetAllInvalidate();

  const [epSocialPageGroupsMap, setStateEpSocialPageGroupsMap] = useState<
    IContextEp["state"]["epSocialPageGroupsMap"]
  >(new Map());
  const [epOrganizationMembershipsMap, setEpOrganizationMembershipsMap] =
    useState<IContextEp["state"]["epOrganizationMembershipsMap"]>(new Map());
  const [epPageConfigsMap, setEpPageConfigsMap] = useState<
    IContextEp["state"]["epPageConfigsMap"]
  >(new Map());
  const [epPageConfigAccountsMap, setEpPageConfigAccountsMap] = useState<
    IContextEp["state"]["epPageConfigAccountsMap"]
  >(new Map());
  const [epSocialAccountsMap, setEpSocialAccountsMap] = useState<
    IContextEp["state"]["epSocialAccountsMap"]
  >(new Map());

  const epPageConfigAccountByPageConfigIdMap: IContextEp["state"]["epPageConfigAccountByPageConfigIdMap"] =
    useMemo(() => {
      const result = new Map();
      dataArrayFromMap(epPageConfigAccountsMap).forEach((pageconfigaccount) =>
        result.set(
          pageconfigaccount.page_config,
          result.has(pageconfigaccount.page_config)
            ? Array.isArray(result.get(pageconfigaccount.page_config))
              ? [
                  ...result.get(pageconfigaccount.page_config),
                  pageconfigaccount,
                ]
              : [result.get(pageconfigaccount.page_config), pageconfigaccount]
            : pageconfigaccount,
        ),
      );
      return result;
    }, [epPageConfigAccountsMap]);

  const setEpSocialPageGroupsMap = useCallback<
    typeof setStateEpSocialPageGroupsMap
  >(
    (...args) => {
      invalidatePageConfigSet();
      setStateEpSocialPageGroupsMap(...args);
    },
    [invalidatePageConfigSet],
  );

  const epOrganizationMembershipsDelete: IContextEp["actions"]["epOrganizationMembershipsDelete"] =
    useCallback(async ({ membership }) => {
      await ApiEpOrganization.Membership.destroy({ membership });
      setEpOrganizationMembershipsMap((organizationMembershipsMap) =>
        produce(organizationMembershipsMap, (draft) => {
          draft.delete(membership.id);
        }),
      );
    }, []);

  const epOrganizationMembershipsGet: IContextEp["actions"]["epOrganizationMembershipsGet"] =
    useCallback(async (...args) => {
      const memberships = await ApiEpOrganization.Membership.list(...args);
      setEpOrganizationMembershipsMap(
        new Map(memberships.map((membership) => [membership.id, membership])),
      );
      return memberships;
    }, []);

  const epOrganizationMembershipsPost: IContextEp["actions"]["epOrganizationMembershipsPost"] =
    useCallback(async ({ organization, role, user }) => {
      const membership = await ApiEpOrganization.Membership.create({
        organization,
        role,
        user,
      });
      setEpOrganizationMembershipsMap((organizationMembershipsMap) =>
        produce(organizationMembershipsMap, (draft) => {
          draft.set(membership["id"], membership);
        }),
      );
      return membership;
    }, []);

  const epOrganizationMembershipsPut: IContextEp["actions"]["epOrganizationMembershipsPut"] =
    useCallback(async (...args) => {
      const membership = await ApiEpOrganization.Membership.update(...args);
      setEpOrganizationMembershipsMap((membershipsMap) =>
        produce(membershipsMap, (draft) => {
          draft.set(membership["id"], membership);
        }),
      );
      return membership;
    }, []);

  const epOrganizationPropagate: IContextEp["actions"]["epOrganizationPropagate"] =
    useCallback(
      (data) => {
        setEpSocialPageGroupsMap((groupsMap) =>
          produce(groupsMap, (draft) => {
            if (data.socialpagegroup_destroyed) {
              data.socialpagegroup_id && draft.delete(data?.socialpagegroup_id);
            } else {
              const group =
                data.socialpagegroup_id && draft.get(data.socialpagegroup_id);
              if (group) {
                group.organization = data.organization_id_after;
                group.is_locked_for_update = false;
                if (group.watermark) {
                  group.watermark =
                    data.watermark_alter_id_map[group.watermark.id];
                  /** TODO: make watermark object without name
                  group.watermark = {
                    id: data.watermark_alter_id_map[group.watermark.id],
                  };
                */
                }
              }
            }
          }),
        );

        setEpPageConfigsMap((pageconfigsMap) =>
          produce(pageconfigsMap, (draft) => {
            data.pageconfig_ids.forEach((pageconfigId: any) => {
              const pageconfig = draft.get(pageconfigId);
              if (pageconfig) {
                pageconfig.organization = data.organization_id_after;
                pageconfig.group = data.socialpagegroup_destroyed
                  ? null
                  : data.socialpagegroup_id;
                if (pageconfig.watermark) {
                  pageconfig.watermark =
                    data.watermark_alter_id_map[pageconfig.watermark.id];
                  /**
                  pageconfig.watermark = {
                    id: data.watermark_alter_id_map[pageconfig.watermark.id],
                  } as IApiEpPageconfig["watermark"];
                */
                }
              }
            });
          }),
        );

        // ToDo: propagate for state of related Posts, PostsProjects, Watermarks when they will be included to state
      },
      [setEpSocialPageGroupsMap],
    );

  const epPageConfigsGet: IContextEp["actions"]["epPageConfigsGet"] =
    useCallback(async (params) => {
      const pageconfigs = await ApiEpPageConfigOld.get(params);
      setEpPageConfigsMap(
        new Map(pageconfigs.map((pageconfig) => [pageconfig.id, pageconfig])),
      );
      return pageconfigs;
    }, []);

  const epPageConfigAccountsGet: IContextEp["actions"]["epPageConfigAccountsGet"] =
    useCallback(async () => {
      const response = await ApiEpPageConfigOld.getAccounts();

      setEpPageConfigAccountsMap(
        new Map(
          response.map((pageconfigaccount) => [
            pageconfigaccount.id,
            pageconfigaccount,
          ]),
        ),
      );
      return response;
    }, []);

  const epPageConfigAccountsByIdGet: IContextEp["actions"]["epPageConfigAccountsByIdGet"] =
    useCallback(
      (id: IApiEpPageConfigOld["id"]) => {
        const extendedPageConfigEp =
          epPageConfigAccountByPageConfigIdMap.get(id);

        return Array.isArray(extendedPageConfigEp)
          ? extendedPageConfigEp
          : extendedPageConfigEp
          ? [extendedPageConfigEp]
          : undefined;
      },
      [epPageConfigAccountByPageConfigIdMap],
    );

  const epPageConfigAccountPatch: IContextEp["actions"]["epPageConfigAccountPatch"] =
    useCallback(async ({ pageConfigAccount }) => {
      const response = await ApiEpPageConfigOld.accountSettingsUpgrade({
        pageConfigAccount,
      });

      setEpPageConfigAccountsMap((accounts) =>
        produce(accounts, (draft) => {
          draft.set(response.id, response);
        }),
      );
      return response;
    }, []);

  const epPageConfigDestroy: IContextEp["actions"]["epPageConfigDestroy"] =
    useCallback(async ({ id, transferPageConfigId }) => {
      const response = await ApiEpPageConfigOld.destroy({
        id,
        transferPageConfigId,
      });
      setEpPageConfigsMap((pageConfigsMap) =>
        produce(pageConfigsMap, (draft) => {
          draft.delete(id);
        }),
      );
      return response;
    }, []);

  const epPageConfigsMoveTo: IContextEp["actions"]["epPageConfigsMoveTo"] =
    useCallback(
      (pageConfigSet) => {
        pageConfigSet.forEach((pageConfigId) => {
          const pageConfig = epPageConfigsMap.get(pageConfigId);
          if (pageConfig) {
            const updatedPageConfig = { ...pageConfig };
            updatedPageConfig.group = null;
            setEpPageConfigsMap((accounts) =>
              produce(accounts, (draft) => {
                draft.set(updatedPageConfig.id, updatedPageConfig);
              }),
            );
          }
        });
      },
      [epPageConfigsMap],
    );

  const epPageConfigPatch: IContextEp["actions"]["epPageConfigPatch"] =
    useCallback(async ({ data, fields }) => {
      const pageconfig = await ApiEpPageConfigOld.update({
        data,
        fields,
      });

      setEpPageConfigsMap((pageConfigsMap) =>
        produce(pageConfigsMap, (draft) => {
          const currentPageConfig = draft.get(pageconfig.id);
          const updatedPageConfig = currentPageConfig
            ? {
                has_publish_permission:
                  currentPageConfig.has_publish_permission,
                ...pageconfig,
              }
            : pageconfig;

          draft.set(updatedPageConfig.id, updatedPageConfig);
        }),
      );
      return pageconfig;
    }, []);

  const epPageConfigPost: IContextEp["actions"]["epPageConfigPost"] =
    useCallback(async (...args) => {
      const socialPageConfigNew = await ApiEpPageConfigOld.create(...args);
      setEpPageConfigsMap((pageConfigsMap) =>
        produce(pageConfigsMap, (draft) => {
          draft.set(socialPageConfigNew.id, socialPageConfigNew);
        }),
      );
      return socialPageConfigNew;
    }, []);

  const epPageConfigPut: IContextEp["actions"]["epPageConfigPut"] = useCallback(
    async ({ data, fields }) => {
      const pageconfig = await ApiEpPageConfigOld.rewrite({
        data,
        fields,
      });

      setEpPageConfigsMap((pageConfigsMap) =>
        produce(pageConfigsMap, (draft) => {
          const currentPageConfig = draft.get(pageconfig.id);
          const updatedPageConfig = currentPageConfig
            ? {
                has_publish_permission:
                  currentPageConfig.has_publish_permission,
                ...pageconfig,
              }
            : pageconfig;
          draft.set(updatedPageConfig.id, updatedPageConfig);
        }),
      );
      return pageconfig;
    },
    [],
  );

  const epPageConfigsByIdGet: IContextEp["actions"]["epPageConfigsByIdGet"] =
    useCallback(async (args) => {
      const pageconfig = await ApiEpPageConfigOld.retrieve(args);
      setEpPageConfigsMap((socialPageConfigsMap) =>
        produce(socialPageConfigsMap, (draft) => {
          const currentPageConfig = draft.get(args.id);
          const updatedPageConfig = currentPageConfig
            ? {
                has_publish_permission:
                  currentPageConfig.has_publish_permission,
                ...pageconfig,
              }
            : pageconfig;
          draft.set(updatedPageConfig.id, updatedPageConfig);
        }),
      );
      return pageconfig;
    }, []);

  const epPageConfigByIdLocalUpdate: IContextEp["actions"]["epPageConfigByIdLocalUpdate"] =
    useCallback((pageConfig) => {
      setEpPageConfigsMap((pageConfigsMap) =>
        produce(pageConfigsMap, (draft) => {
          const current = draft.get(pageConfig.id);
          if (!current) return;
          draft.set(current.id, { ...current, ...pageConfig });
        }),
      );
    }, []);

  const epInstagramPostsByPageConfigIdGet: IContextEp["actions"]["epInstagramPostsByPageConfigIdGet"] =
    useCallback(async (args) => ApiEpInstagram.post.list(args), []);

  const epSocialAccountsGet: IContextEp["actions"]["epSocialAccountsGet"] =
    useCallback(async () => {
      const response = await ApiEpSocialAccount.list();

      setEpSocialAccountsMap(
        new Map(
          response.map((pageconfigaccount) => [
            pageconfigaccount.id,
            pageconfigaccount,
          ]),
        ),
      );
      return response;
    }, []);

  const epSocialPageGroupsByIdLocalUpdate: IContextEp["actions"]["epSocialPageGroupsByIdLocalUpdate"] =
    useCallback(
      (group) => {
        setEpSocialPageGroupsMap((socialpagegroupsMap) =>
          produce(socialpagegroupsMap, (draft) => {
            const current = draft.get(group.id);
            if (!current) return;
            draft.set(current.id, { ...current, ...group });
          }),
        );
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsPageConfigSetUpdateLocal = useCallback<
    IContextEp["actions"]["epSocialPageGroupsPageConfigSetUpdateLocal"]
  >(
    ({ currentGroupId, targetGroupId, pageConfigId }) => {
      const currentGroup = currentGroupId
        ? epSocialPageGroupsMap.get(currentGroupId)
        : null;
      const targetGroup = targetGroupId
        ? epSocialPageGroupsMap.get(targetGroupId)
        : null;
      if (currentGroup) {
        const currentGroupCopy = { ...currentGroup };
        const pageConfigIndex = currentGroupCopy.pageconfig_set.findIndex(
          (id) => id === pageConfigId,
        );
        epSocialPageGroupsByIdLocalUpdate({
          ...currentGroupCopy,
          pageconfig_set: [
            ...[...currentGroupCopy.pageconfig_set].splice(0, pageConfigIndex),
            ...[...currentGroupCopy.pageconfig_set].splice(
              pageConfigIndex + 1,
              currentGroupCopy.pageconfig_set.length,
            ),
          ],
        });
      }
      if (targetGroup) {
        const targetGroupCopy = { ...targetGroup };
        targetGroupCopy.pageconfig_set = [
          ...targetGroupCopy.pageconfig_set,
          pageConfigId,
        ];
        epSocialPageGroupsByIdLocalUpdate(targetGroupCopy);
      }
    },
    [epSocialPageGroupsByIdLocalUpdate, epSocialPageGroupsMap],
  );

  const epSocialPageGroupsByIdGet: IContextEp["actions"]["epSocialPageGroupsByIdGet"] =
    useCallback(
      async (...args) => {
        const socialPageGroup = await ApiEpSocialPageGroup.retrieve(...args);
        setEpSocialPageGroupsMap((socialpagegroupsMap) =>
          produce(socialpagegroupsMap, (draft) => {
            draft.set(socialPageGroup.id, socialPageGroup);
          }),
        );
        return socialPageGroup;
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsCancelDeferred: IContextEp["actions"]["epSocialPageGroupsCancelDeferred"] =
    useCallback(
      async (...args) => {
        const socialPageGroup =
          await ApiEpSocialPageGroup.common.cancelDeffered(...args);
        setEpSocialPageGroupsMap((socialpagegroupsMap) =>
          produce(socialpagegroupsMap, (draft) => {
            draft.set(socialPageGroup.id, socialPageGroup);
          }),
        );
        return socialPageGroup;
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsDelete: IContextEp["actions"]["epSocialPageGroupsDelete"] =
    useCallback(
      async ({ id }) => {
        await ApiEpSocialPageGroup.destroy({ id });
        setEpSocialPageGroupsMap((socialPageGroupsMap) =>
          produce(socialPageGroupsMap, (draft) => {
            draft.delete(id);
          }),
        );
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsGet: IContextEp["actions"]["epSocialPageGroupsGet"] =
    useCallback(
      async ({ allOrganizationContexts, fields }) => {
        const socialPageGroupsList = await ApiEpSocialPageGroup.list({
          query: { all_organization_contexts: allOrganizationContexts, fields },
        });
        setEpSocialPageGroupsMap((socialPageGroupsMap) =>
          produce(socialPageGroupsMap, (draft) => {
            draft.clear();
            socialPageGroupsList.forEach((socialPageGroup) => {
              draft.set(socialPageGroup.id, socialPageGroup);
            });
          }),
        );
        return socialPageGroupsList;
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsPatch: IContextEp["actions"]["epSocialPageGroupsPatch"] =
    useCallback(
      async (...args) => {
        const socialPageGroup = await ApiEpSocialPageGroup.update(...args);
        setEpSocialPageGroupsMap((socialPageGroupsMap) =>
          produce(socialPageGroupsMap, (draft) => {
            draft.set(socialPageGroup["id"], socialPageGroup);
          }),
        );
        return socialPageGroup;
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsPost: IContextEp["actions"]["epSocialPageGroupsPost"] =
    useCallback(
      async (...args) => {
        const socialPageGroup = await ApiEpSocialPageGroup.create(...args);
        setEpSocialPageGroupsMap((socialPageGroupsMap) =>
          produce(socialPageGroupsMap, (draft) => {
            draft.set(socialPageGroup.id, socialPageGroup);
          }),
        );
        return socialPageGroup;
      },
      [setEpSocialPageGroupsMap],
    );

  const epSocialPageGroupsPut: IContextEp["actions"]["epSocialPageGroupsPut"] =
    useCallback(
      async (...args) => {
        const socialPageGroup = await ApiEpSocialPageGroup.rewrite(...args);
        setEpSocialPageGroupsMap((socialPageGroupsMap) =>
          produce(socialPageGroupsMap, (draft) => {
            draft.set(socialPageGroup["id"], {
              ...socialPageGroup,
              organization: socialPageGroup.organization_after_update,
            });
          }),
        );
        queryClient.invalidateQueries(useSocialPageGroupSetQuery.getKey());
        return socialPageGroup;
      },
      [queryClient, setEpSocialPageGroupsMap],
    );

  const epUpdatePageConfigsWhenNewAccountConnected: IContextEp["actions"]["epUpdatePageConfigsWhenNewAccountConnected"] =
    useCallback(
      async ({ pageConfigAccountIds, fields, targetSocialPageGroup }) => {
        const pageConfigs = await epPageConfigsGet({});
        await epPageConfigAccountsGet();
        if (pageConfigAccountIds && targetSocialPageGroup) {
          const newPageConfigs = pageConfigs.filter((pageConfig) =>
            pageConfigAccountIds.some(
              (pageConfigAccountId) => pageConfigAccountId === pageConfig.id,
            ),
          );
          const newPageConfgsIds = newPageConfigs.map(
            (pageConfig) => pageConfig.id,
          );
          if (!newPageConfgsIds.length) return;

          const { id, name, pageconfig_set } = targetSocialPageGroup;
          const targetGroupPageConfigSetWithoutDoubles = pageconfig_set.filter(
            (pageConfigId) =>
              !newPageConfgsIds.some(
                (newPageConfigId) => newPageConfigId === pageConfigId,
              ),
          );
          const updatedPageConfigSet = [
            ...targetGroupPageConfigSetWithoutDoubles,
            ...newPageConfgsIds,
          ];
          await epSocialPageGroupsPatch({
            fields,
            socialPageGroup: { id, name, pageconfig_set: updatedPageConfigSet },
            organizationContext: targetSocialPageGroup.organization,
          });
          await epPageConfigsGet({});
        }
        return;
      },
      [epPageConfigAccountsGet, epPageConfigsGet, epSocialPageGroupsPatch],
    );

  return useMemo(
    () => (
      <ContextEp.Provider
        value={{
          actions: {
            epInstagramPostsByPageConfigIdGet,
            epOrganizationMembershipsDelete,
            epOrganizationMembershipsGet,
            epOrganizationMembershipsPost,
            epOrganizationMembershipsPut,
            epOrganizationPropagate,
            epPageConfigAccountPatch,
            epPageConfigAccountsByIdGet,
            epPageConfigAccountsGet,
            epPageConfigByIdLocalUpdate,
            epPageConfigDestroy,
            epPageConfigPatch,
            epPageConfigPost,
            epPageConfigPut,
            epPageConfigsByIdGet,
            epPageConfigsGet,
            epPageConfigsMoveTo,
            epSocialAccountsGet,
            epSocialPageGroupsByIdGet,
            epSocialPageGroupsByIdLocalUpdate,
            epSocialPageGroupsCancelDeferred,
            epSocialPageGroupsDelete,
            epSocialPageGroupsGet,
            epSocialPageGroupsPageConfigSetUpdateLocal,
            epSocialPageGroupsPatch,
            epSocialPageGroupsPost,
            epSocialPageGroupsPut,
            epUpdatePageConfigsWhenNewAccountConnected,
          },
          state: {
            epOrganizationMembershipsMap,
            epPageConfigAccountByPageConfigIdMap,
            epPageConfigAccountsMap,
            epPageConfigsMap,
            epSocialAccountsMap,
            epSocialPageGroupsMap,
          },
        }}
      >
        {children}
      </ContextEp.Provider>
    ),
    [
      epInstagramPostsByPageConfigIdGet,
      epOrganizationMembershipsDelete,
      epOrganizationMembershipsGet,
      epOrganizationMembershipsPost,
      epOrganizationMembershipsPut,
      epOrganizationPropagate,
      epPageConfigAccountPatch,
      epPageConfigAccountsByIdGet,
      epPageConfigAccountsGet,
      epPageConfigByIdLocalUpdate,
      epPageConfigDestroy,
      epPageConfigPatch,
      epPageConfigPost,
      epPageConfigPut,
      epPageConfigsByIdGet,
      epPageConfigsGet,
      epPageConfigsMoveTo,
      epSocialAccountsGet,
      epSocialPageGroupsByIdGet,
      epSocialPageGroupsByIdLocalUpdate,
      epSocialPageGroupsCancelDeferred,
      epSocialPageGroupsDelete,
      epSocialPageGroupsGet,
      epSocialPageGroupsPageConfigSetUpdateLocal,
      epSocialPageGroupsPatch,
      epSocialPageGroupsPost,
      epSocialPageGroupsPut,
      epUpdatePageConfigsWhenNewAccountConnected,
      epOrganizationMembershipsMap,
      epPageConfigAccountByPageConfigIdMap,
      epPageConfigAccountsMap,
      epPageConfigsMap,
      epSocialAccountsMap,
      epSocialPageGroupsMap,
      children,
    ],
  );
};
